/**
 * TemplateLibrary — User-created templates from Content Studio.
 *
 * Lists every social + email template saved via "Save as template" in
 * Content Studio. Each row offers: Edit (jumps back into Studio with
 * ?template=<id>), Duplicate, Rename, and Delete.
 *
 * Pure localStorage-backed for now (see `lib/marketingTemplatesStore.js`).
 * Designed so a future `/api/marketing/templates` endpoint can drop in
 * without changing the page.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import MarketingShell from "@/components/MarketingShell";
import useConfirm from "@/hooks/useConfirm";
import usePrompt from "@/hooks/usePrompt";
import { toast } from "sonner";
import {
  FolderOpen, Plus, Mail, Image as ImageIcon, Edit3, Copy, Trash2, Search,
} from "lucide-react";
import {
  useTemplatesList, deleteTemplate, duplicateTemplate, renameTemplate,
} from "@/lib/marketingTemplatesStore";

const KIND_META = {
  email:  { label: "Email", icon: Mail, color: "#2E63B8" },
  social: { label: "Social", icon: ImageIcon, color: "#7A33FF" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

export default function TemplateLibrary() {
  const navigate = useNavigate();
  const items = useTemplatesList();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [filter, setFilter] = useState("all"); // all | email | social
  const [query, setQuery] = useState("");

  const filtered = items
    .filter((t) => filter === "all" || t.kind === filter)
    .filter((t) => !query || (t.name || "").toLowerCase().includes(query.toLowerCase()));

  const handleEdit = (tpl) => navigate(`/marketing/create/studio?template=${tpl.id}`);

  const handleRename = async (tpl) => {
    const name = await prompt({
      title: "Rename template",
      body: "Pick a new name for this template.",
      defaultValue: tpl.name,
      placeholder: "Template name",
      submitLabel: "Save",
    });
    if (!name || name.trim() === tpl.name) return;
    await renameTemplate(tpl.id, name.trim());
    toast.success(`Renamed to "${name.trim()}"`);
  };

  const handleDuplicate = async (tpl) => {
    const copy = await duplicateTemplate(tpl.id);
    if (copy) toast.success(`Duplicated as "${copy.name}"`);
  };

  const handleDelete = async (tpl) => {
    const ok = await confirm({
      title: "Delete this template?",
      body: `"${tpl.name}" will be removed from your library. This cannot be undone.`,
      confirmLabel: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await deleteTemplate(tpl.id);
    toast.success("Template deleted");
  };

  const headerActions = (
    <button
      className="btn btn-primary"
      onClick={() => navigate("/marketing/create/studio")}
      data-testid="new-template-btn"
    >
      <Plus style={{ width: 13, height: 13 }} /> New template
    </button>
  );

  return (
    <MarketingShell
      title="Template Library"
      subtitle={items.length === 0 ? "Your saved templates will appear here" : `${items.length} saved templates`}
      actions={headerActions}
    >
      <div data-testid="template-library-page">
        {/* Filters */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div className="mkt-segmented">
            {[
              { k: "all", l: "All" },
              { k: "email", l: "Email" },
              { k: "social", l: "Social" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={filter === f.k ? "active" : ""}
                data-testid={`library-filter-${f.k}`}
              >
                {f.l}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative" }}>
            <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "var(--text-tertiary)" }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates…"
              style={{ paddingLeft: 30, fontSize: 12, width: 220, height: 30 }}
              className="input"
              data-testid="library-search"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div
            className="mkt-card mkt-card-pad"
            style={{ textAlign: "center", padding: 60 }}
            data-testid="library-empty"
          >
            <FolderOpen style={{ width: 36, height: 36, color: "var(--text-tertiary)", margin: "0 auto 12px" }} />
            <h3 style={{ fontFamily: "var(--font-head)", color: "var(--navy)", fontWeight: 620, marginBottom: 6 }}>
              {items.length === 0 ? "No saved templates yet" : "No matches"}
            </h3>
            <p style={{ fontSize: 12.5, color: "var(--text-tertiary)", lineHeight: 1.6, marginBottom: 16 }}>
              {items.length === 0
                ? "Open Content Studio, design a social card or email, then click \u201CSave as template\u201D to add it to your library."
                : "Try a different search or filter."}
            </p>
            {items.length === 0 && (
              <button
                className="btn btn-primary"
                onClick={() => navigate("/marketing/create/studio")}
                data-testid="library-empty-cta"
              >
                <Plus style={{ width: 13, height: 13 }} /> Open Content Studio
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {filtered.map((tpl) => {
              const meta = KIND_META[tpl.kind] || KIND_META.social;
              const Icon = meta.icon;
              return (
                <div
                  key={tpl.id}
                  className="mkt-card"
                  style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}
                  data-testid={`template-card-${tpl.id}`}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: `${meta.color}15`, color: meta.color,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Icon style={{ width: 15, height: 15 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{ fontSize: 13.5, fontWeight: 580, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        data-testid={`template-name-${tpl.id}`}
                        title={tpl.name}
                      >
                        {tpl.name}
                      </div>
                      <div style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>
                        {meta.label}
                        {tpl.kind === "social" && tpl.size ? ` · ${tpl.size}` : ""}
                        {tpl.kind === "email" && (tpl.blocks?.length || 0) > 0 ? ` · ${tpl.blocks.length} blocks` : ""}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 10.5, color: "var(--text-tertiary)", display: "flex", justifyContent: "space-between" }}>
                    <span>Created {fmtDate(tpl.createdAt)}</span>
                    <span>Updated {fmtDate(tpl.updatedAt)}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <button
                      className="btn btn-primary"
                      style={{ flex: 1, height: 30, fontSize: 12 }}
                      onClick={() => handleEdit(tpl)}
                      data-testid={`template-edit-${tpl.id}`}
                    >
                      <Edit3 style={{ width: 12, height: 12 }} /> Edit
                    </button>
                    <button
                      className="btn"
                      style={{ height: 30, padding: "0 9px" }}
                      onClick={() => handleDuplicate(tpl)}
                      data-testid={`template-duplicate-${tpl.id}`}
                      title="Duplicate"
                    >
                      <Copy style={{ width: 12, height: 12 }} />
                    </button>
                    <button
                      className="btn"
                      style={{ height: 30, padding: "0 9px" }}
                      onClick={() => handleRename(tpl)}
                      data-testid={`template-rename-${tpl.id}`}
                      title="Rename"
                    >
                      Aa
                    </button>
                    <button
                      className="btn"
                      style={{ height: 30, padding: "0 9px", color: "var(--error)" }}
                      onClick={() => handleDelete(tpl)}
                      data-testid={`template-delete-${tpl.id}`}
                      title="Delete"
                    >
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MarketingShell>
  );
}
